import { UseFormReturn } from 'react-hook-form';
import { IAbookTab } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import { DocumentService } from '@services/DocumentService';
import { FormulaManager } from '@utils/managers/FormulaManager';
import type { IFormulaRequestCache } from '@utils/managers/IFormulaRequestCache';
import { createFormulaManagerEvaluator, IFormulaEvaluator } from '@utils/validation/document/formulaEvaluator';
import { convertListToMap } from './documentUtils';

export const hasAbookTabVisibilityRules = (tabs?: IAbookTab[]): tabs is IAbookTab[] =>
    !!tabs?.some((tab) => tab.visibilityRules);

/**
 * Вкладки, условие показа которых истинно. Ошибка формулы вкладку не прячет: правило — удобство выбора, а окно, в
 * котором из-за опечатки в конфиге не осталось вкладок, не даёт выбрать никого.
 */
export const resolveVisibleAbookTabs = async (
    tabs: IAbookTab[],
    evaluator: Pick<IFormulaEvaluator, 'evalBool'>,
): Promise<IAbookTab[]> => {
    const visible = await Promise.all(
        tabs.map((tab) =>
            evaluator.evalBool(tab.visibilityRules, true).catch((error: unknown) => {
                console.error(error);
                return true;
            }),
        ),
    );
    return tabs.filter((_, index) => visible[index]);
};

const mapTabRules = (tabs: IAbookTab[], read: (manager: FormulaManager) => string[]) => {
    const result = new Set<string>();
    for (const tab of tabs) {
        if (!tab.visibilityRules) continue;
        try {
            read(new FormulaManager(tab.visibilityRules)).forEach((name) => result.add(name));
        } catch (error) {
            console.error(error);
        }
    }
    return [...result];
};

/** Пути RHF полей, от которых зависят условия вкладок: их смена пересчитывает видимость. */
export const getAbookTabsWatchFields = <TFieldValues extends object>(
    tabs: IAbookTab[],
    fields: Record<string, IFieldElem>,
    formMethods?: UseFormReturn<TFieldValues>,
) =>
    mapTabRules(tabs, (manager) => {
        manager.Init(fields, formMethods);
        return manager.GetWatchFields().filter(Boolean);
    });

const getAbookTabsDocumentAttrs = (tabs: IAbookTab[]) =>
    mapTabRules(tabs, (manager) => {
        manager.Init();
        return manager.GetDependencyWatchFields();
    });

export const resolveFormAbookTabs = <TFieldValues extends object>(
    tabs: IAbookTab[],
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<TFieldValues>,
    isEdit: boolean,
    isNew: boolean,
    requestCache?: IFormulaRequestCache,
) => resolveVisibleAbookTabs(tabs, createFormulaManagerEvaluator(fields, formMethods, isEdit, isNew, requestCache));

/**
 * Видимость вкладок окна выбора, открытого действием без формы: значения полей — сохранённые в документе
 * (`getFormData`), формула считается как в режиме просмотра карточки. Не удалось прочитать документ — видны все
 * вкладки, как без условий.
 */
export const resolveDocumentAbookTabs = async (docId: string | undefined, tabs: IAbookTab[]) => {
    const attrs = getAbookTabsDocumentAttrs(tabs);
    let fields: Record<string, IFieldElem> = {};
    if (docId && attrs.length > 0) {
        try {
            const response = await DocumentService.getFormData(docId, attrs);
            fields = convertListToMap(response.data.fields);
        } catch (error) {
            console.error(error);
            return tabs;
        }
    }
    return resolveVisibleAbookTabs(tabs, createFormulaManagerEvaluator(fields, undefined, false, false));
};

/** Тот же набор вкладок — прежний массив: окно не переключает вкладку на пересчёте с тем же результатом. */
export const keepSameAbookTabs = (prev: IAbookTab[] | undefined, next: IAbookTab[]) =>
    prev && prev.length === next.length && prev.every((tab, index) => tab === next[index]) ? prev : next;

/** Активная вкладка после смены набора видимых: прежняя, если осталась (та же или с тем же именем), иначе первая. */
export const pickActiveAbookTab = (tabs: IAbookTab[], current?: IAbookTab): IAbookTab | undefined => {
    if (current) {
        const same = tabs.find((tab) => tab === current) ?? tabs.find((tab) => tab.name === current.name);
        if (same) return same;
    }
    return tabs[0];
};

/** Вкладка сотрудников (остальные — группы, подразделения): у неё поиск по ФИО, должности и подразделению. */
export const isPersonTab = (tab: IAbookTab) => tab.child?.toLowerCase() === 'person';
